import React from 'react';
import { Search, Check, Play } from 'lucide-react';

const HowItWorks = ({ language }) => {
  const texts = {
    en: {
      title: 'How It Works',
      subtitle: 'Three simple steps to get started',
      steps: [
        {
          number: '01',
          icon: Search,
          title: 'Browse Games',
          description: 'Explore our extensive collection of mobile games and apps across different categories'
        },
        {
          number: '02',
          icon: Check,
          title: 'Get Started',
          description: 'Select your favorites and access them directly on your mobile device instantly'
        },
        {
          number: '03',
          icon: Play,
          title: 'Start Playing',
          description: 'Launch your games and apps immediately and enjoy endless entertainment'
        }
      ]
    },
    sl: {
      title: 'Kako deluje',
      subtitle: 'Trije preprosti koraki za začetek',
      steps: [
        {
          number: '01',
          icon: Search,
          title: 'Prebrskaj igre',
          description: 'Raziščite našo obsežno zbirko mobilnih iger in aplikacij v različnih kategorijah'
        },
        {
          number: '02',
          icon: Check,
          title: 'Začni',
          description: 'Izberite svoje najljubše in jih takoj dostopajte neposredno na vašo mobilno napravo'
        },
        {
          number: '03',
          icon: Play,
          title: 'Začni igrati',
          description: 'Zaženite igre in aplikacije takoj in uživajte v neskončni zabavi'
        }
      ]
    }
  };

  const t = texts[language];

  return (
    <section id="section-3" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {t.steps.map((step, index) => (
            <div
              key={index}
              className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                {step.number}
              </div>
              
              <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <step.icon className="w-10 h-10 text-blue-600" />
              </div>
              
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                {step.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Process Image */}
        <div className="mt-20 relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-3xl opacity-20 blur-xl"></div>
              <div className="relative bg-white rounded-2xl p-6 shadow-2xl">
                <img
                  src="/images/download-process.jpeg"
                  alt={language === 'en' ? 'Mobile game loading' : 'Nalaganje mobilne igre'}
                  className="w-full h-auto rounded-xl"
                />
                
                {/* Animated progress indicator */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">
                      {language === 'en' ? 'Loading...' : 'Nalaganje...'}
                    </span>
                    <span className="text-xs font-bold text-blue-600">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full animate-progress" style={{width: '85%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 space-y-4">
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2">
                <Check className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-blue-600">
                  {language === 'en' ? 'Fast & Secure' : 'Hitro in varno'}
                </span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900">
                {language === 'en' ? 'Quick Access' : 'Hitri dostop'}
              </h3>
              <p className="text-lg text-gray-600">
                {language === 'en' 
                  ? 'Our optimized system ensures you get access to your games and apps in seconds, not minutes.'
                  : 'Naš optimiziran sistem zagotavlja, da dobite dostop do svojih iger in aplikacij v sekundah, ne minutah.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
